// Exercise catalog seed (gym-tracker-spec.md section 6 + section 7). Structural
// methodology only, no private health data. Weights are stored TOTAL kg; per_side
// means Marko logs the per-hand number and the app doubles it internally.
//
// increment is in TOTAL kg: a dumbbell that jumps 2.5 kg per hand has increment 5.
// incrementByGym overrides that where a gym's equipment really steps differently —
// currently only Fitness First's dumbbell rack (2 kg/hand = 4 total, vs FitX's
// 2.5 kg/hand = 5). Plate- and pin-loaded machines are left alone until a real
// mismatch shows up; add an override here the moment one does.
// elbow/forearm feel tracking is first-class on pulling and curl movements. It
// began as management for a medial-epicondyle tendinosis (resolved 2026-09) and is
// kept as a monitor, since load steps on pulls are only safe while feel stays quiet.

import type { Exercise } from '../domain/types';

type Seed = Omit<Exercise, 'archived'> & { archived?: boolean };

function ex(s: Seed): Exercise {
  return { archived: false, ...s };
}

export const EXERCISES: Exercise[] = [
  // --- Push (Upper A) ---
  ex({
    id: 'dips',
    name: 'Dips (weighted)',
    muscleGroup: 'chest',
    // Switched 2026-09 from AMRAP (set 1 parked at 9 reps @ +10 kg since August)
    // to a heavy top set + back-offs: top set 6-8 @ 1 RIR, never to failure;
    // back-offs at -10% of ABSOLUTE load (BW + added), roughly +5 kg, 8-12 reps.
    structure: 'ramp_top_backoff',
    defaultRepRange: { bottom: 6, top: 8 },
    // 1 kg step: dips are loaded with fixed belts/balls (6 kg, 10 kg), which don't
    // sit on a 2.5 grid — a whole-kg increment makes every real implement reachable
    // with the +/- taps instead of forcing an off-grid type-in.
    increment: 1,
    equipmentArea: 'upstairs',
    weightEntry: 'total',
    bodyweight: true,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Main push lift. Ramp (BW x5, +5 x3), then ONE heavy top set of 6-8 at 1 RIR — never to failure, 2 s lowering, no bounce at the bottom. Two back-offs ~+5 kg for 8-12; the last may go to failure. No jump-up negatives / rest-pause (triceps tendon). If the tendon logs "aware" twice running, go back to straight sets at +5.',
  }),
  ex({
    id: 'bench-machine',
    name: 'Bench machine',
    muscleGroup: 'chest',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 8, top: 12 },
    increment: 2.5,
    equipmentArea: 'downstairs',
    weightEntry: 'total',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: '',
  }),
  ex({
    id: 'incline-db-press',
    name: 'Incline DB press',
    muscleGroup: 'chest',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 8, top: 12 },
    increment: 5,
    incrementByGym: { 'fitness-first': 4 },
    equipmentArea: 'downstairs',
    weightEntry: 'per_side',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Preferred when the incline machine is busy.',
  }),
  ex({
    id: 'flat-db-press',
    name: 'Flat DB press',
    muscleGroup: 'chest',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 8, top: 12 },
    increment: 5,
    incrementByGym: { 'fitness-first': 4 },
    equipmentArea: 'downstairs',
    weightEntry: 'per_side',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Flat-bench dumbbell press. Selectable sub for Bench machine on Upper A (do NOT log flat work under Incline DB press).',
  }),
  ex({
    id: 'db-ohp',
    name: 'DB overhead press',
    muscleGroup: 'front_delt',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 8, top: 12 },
    increment: 5,
    incrementByGym: { 'fitness-first': 4 },
    equipmentArea: 'either',
    weightEntry: 'per_side',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Default when the barbell is taken. Otherwise use barbell ramp + top set.',
  }),
  ex({
    id: 'barbell-ohp',
    name: 'Barbell overhead press',
    muscleGroup: 'front_delt',
    structure: 'ramp_top_backoff',
    defaultRepRange: { bottom: 6, top: 8 },
    increment: 2.5,
    equipmentArea: 'either',
    weightEntry: 'total',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Ramp + top set when the barbell is free.',
  }),

  // --- Back / pull (feel-tracked) ---
  ex({
    id: 'row-machine',
    name: 'Row machine',
    muscleGroup: 'back',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 10, top: 12 },
    increment: 2.5,
    equipmentArea: 'downstairs',
    weightEntry: 'total',
    bodyweight: false,
    tracksElbowFeel: true,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Chest-supported. Log elbow feel after the set.',
  }),
  ex({
    id: 'lat-pulldown',
    name: 'Lat pulldown',
    muscleGroup: 'back',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 10, top: 12 },
    increment: 2.5,
    equipmentArea: 'either',
    weightEntry: 'total',
    bodyweight: false,
    tracksElbowFeel: true,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Reintroduced gradually. Log elbow feel after the set.',
  }),
  ex({
    id: 'cable-row',
    name: 'Cable row',
    muscleGroup: 'back',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 10, top: 12 },
    increment: 2.5,
    equipmentArea: 'downstairs',
    weightEntry: 'total',
    bodyweight: false,
    tracksElbowFeel: true,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Log elbow feel after the set.',
  }),
  ex({
    id: 'chest-supported-row',
    name: 'Chest-supported row',
    muscleGroup: 'back',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 10, top: 12 },
    increment: 5,
    equipmentArea: 'downstairs',
    weightEntry: 'per_side',
    bodyweight: false,
    tracksElbowFeel: true,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Downstairs plate-loaded chest-supported row (working ~35 kg per side). Enter per-side load; the app doubles it. Chest support keeps grip/traction off the medial elbow — the elbow-safe default row for Upper B. Log elbow feel after the set.',
  }),
  ex({
    id: 'straight-arm-pulldown',
    name: 'Straight-arm pulldown',
    muscleGroup: 'back',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 12, top: 15 },
    increment: 2.5,
    equipmentArea: 'downstairs',
    weightEntry: 'total',
    bodyweight: false,
    tracksElbowFeel: true,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Straight-arm = elbow extended under load. Watch the feel signal. Out of Upper B while pull-ups are reintroduced (one new elbow load at a time).',
  }),
  ex({
    id: 'assisted-pull-up',
    name: 'Pull-up (assisted)',
    muscleGroup: 'back',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 5, top: 8 },
    // Logged as ADDED load like dips: assistance is negative (-30 = 30 kg of help).
    // A weight "increase" of 2.5 therefore means 2.5 kg LESS assistance.
    increment: 2.5,
    equipmentArea: 'either',
    weightEntry: 'total',
    bodyweight: true,
    tracksElbowFeel: true,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: true,
    notes: 'Reintroduction phase. Neutral grip, stop ~3 RIR, never to failure. Start each rep from a slight elbow bend: no dead hang at the bottom. Log elbow feel every set and again next morning; "aware" twice running = step back, any pain = stop. Cut assistance only while feel stays quiet.',
  }),
  ex({
    id: 'butterfly-reverse',
    name: 'Butterfly reverse (rear delts)',
    muscleGroup: 'rear_delt',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 12, top: 15 },
    increment: 2.5,
    equipmentArea: 'downstairs',
    weightEntry: 'total',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Strict tempo. Primary elbow-safe back exercise.',
  }),

  // --- Biceps / triceps (forearm-tracked curls) ---
  ex({
    id: 'biceps-curl',
    name: 'Biceps curl',
    muscleGroup: 'biceps',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 8, top: 12 },
    increment: 5,
    incrementByGym: { 'fitness-first': 4 },
    equipmentArea: 'either',
    weightEntry: 'per_side',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: true,
    tracksGrip: true,
    neverToFailure: false,
    notes: 'Use straps to cut grip load. Note grip variant (supinated vs hammer). Deliberately light to build the mind-muscle connection.',
  }),
  ex({
    id: 'incline-db-curl',
    name: 'Incline DB curl',
    muscleGroup: 'biceps',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 10, top: 12 },
    increment: 5,
    incrementByGym: { 'fitness-first': 4 },
    equipmentArea: 'downstairs',
    weightEntry: 'per_side',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: true,
    tracksGrip: true,
    neverToFailure: false,
    notes: 'Optional second arm movement. Joint-angle variant, sometimes tolerated when standard curls are not.',
  }),
  ex({
    id: 'tricep-pushdown',
    name: 'Tricep pushdowns',
    muscleGroup: 'triceps',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 10, top: 12 },
    increment: 2.5,
    equipmentArea: 'downstairs',
    weightEntry: 'total',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: '',
  }),
  ex({
    id: 'overhead-tricep-ext',
    name: 'Overhead triceps extension',
    muscleGroup: 'triceps',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 10, top: 12 },
    increment: 2.5,
    equipmentArea: 'downstairs',
    weightEntry: 'total',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Optional second triceps movement.',
  }),

  // --- Delts (isolation) ---
  ex({
    id: 'side-raise-db',
    name: 'Side raises (DB)',
    muscleGroup: 'side_delt',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 10, top: 15 },
    increment: 2,
    incrementByGym: { 'fitness-first': 4 },
    equipmentArea: 'either',
    weightEntry: 'per_side',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: '',
  }),
  ex({
    id: 'side-raise-machine',
    name: 'Side raise machine',
    muscleGroup: 'side_delt',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 10, top: 15 },
    increment: 2.5,
    equipmentArea: 'upstairs',
    weightEntry: 'total',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: '',
  }),

  // --- Legs ---
  ex({
    id: 'leg-press',
    name: 'Leg press',
    muscleGroup: 'quad',
    structure: 'ramp_top_backoff',
    defaultRepRange: { bottom: 6, top: 8 },
    increment: 5,
    equipmentArea: 'downstairs',
    weightEntry: 'total',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Ramp + heavy top set (6-8, 1 RIR, never to failure) + 2 back-offs at -10% for 8-12. Working toward 200.',
  }),
  ex({
    id: 'bulgarian-split-squat',
    name: 'Bulgarian split squat',
    muscleGroup: 'quad',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 8, top: 12 },
    increment: 5,
    incrementByGym: { 'fitness-first': 4 },
    equipmentArea: 'either',
    weightEntry: 'per_side',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: true,
    notes: 'Per leg, start the weaker right leg first. Balance-dependent: never to failure, cap at ~1 RIR.',
  }),
  ex({
    id: 'leg-extension',
    name: 'Leg extensions',
    muscleGroup: 'quad',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 10, top: 15 },
    increment: 2.5,
    equipmentArea: 'upstairs',
    weightEntry: 'total',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Upstairs pin-loaded machine (the heavier scale, ~70-75kg). Leg top-up / quad frequency. The downstairs plate-loaded unit is a separate exercise (leg-extension-downstairs) so the two loading scales never collide in one progression history.',
  }),
  ex({
    id: 'leg-extension-downstairs',
    name: 'Leg extension (downstairs plate)',
    muscleGroup: 'quad',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 10, top: 15 },
    increment: 2.5,
    equipmentArea: 'downstairs',
    weightEntry: 'total',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Downstairs free/plate-loaded leg extension (lighter scale, ~45kg). Distinct from the upstairs pin machine so loads track independently. Use whichever is free and pick the matching exercise so "beat this" stays clean.',
  }),
  ex({
    id: 'seated-leg-curl',
    name: 'Seated leg curl',
    muscleGroup: 'hamstring',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 10, top: 12 },
    increment: 2.5,
    equipmentArea: 'upstairs',
    weightEntry: 'total',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: '',
  }),
  ex({
    id: 'lying-leg-curl',
    name: 'Lying leg curl',
    muscleGroup: 'hamstring',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 10, top: 12 },
    increment: 2.5,
    equipmentArea: 'downstairs',
    weightEntry: 'total',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: '',
  }),
  ex({
    id: 'nordic-curl',
    name: 'Nordic curls',
    muscleGroup: 'hamstring',
    structure: 'amrap',
    defaultRepRange: { bottom: 5, top: 12 },
    increment: 2.5,
    equipmentArea: 'upstairs',
    weightEntry: 'total',
    bodyweight: true,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Posterior chain. AMRAP, assisted reps with arms on the last set. Being re-added.',
  }),
  ex({
    id: 'calf-raise',
    name: 'Calf raises',
    muscleGroup: 'calf',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 12, top: 20 },
    increment: 5,
    equipmentArea: 'either',
    weightEntry: 'total',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Optional.',
  }),

  // --- Core (no dead-hang movements: elbow traction) ---
  ex({
    id: 'captains-chair-raise',
    name: 'Captain\'s chair / lying leg raise',
    muscleGroup: 'core',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 8, top: 12 },
    increment: 2.5,
    equipmentArea: 'either',
    weightEntry: 'total',
    bodyweight: true,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Avoid dead-hang movements (hanging leg raises) due to elbow traction. Use captain\'s chair or lying.',
  }),
  ex({
    id: 'plank',
    name: 'Plank',
    muscleGroup: 'core',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 30, top: 60 },
    increment: 2.5,
    equipmentArea: 'either',
    weightEntry: 'total',
    bodyweight: true,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    timed: true,
    notes: 'Timed hold. The set value is SECONDS held (not reps); the app shows a countdown timer and tracks last time to beat. Neutral spine, brace, no sagging (good for the L4/5 core work).',
  }),
  ex({
    id: 'russian-twist',
    name: 'Russian twists',
    muscleGroup: 'core',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 20, top: 30 },
    increment: 2.5,
    equipmentArea: 'either',
    weightEntry: 'total',
    bodyweight: true,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: '',
  }),
  ex({
    id: 'back-extension',
    name: 'Back extension (hyperextension)',
    muscleGroup: 'core',
    structure: 'straight_sets',
    defaultRepRange: { bottom: 12, top: 20 },
    increment: 2.5,
    equipmentArea: 'either',
    weightEntry: 'total',
    bodyweight: true,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: true,
    notes: 'Lower-back / erector + glute-ham (roman chair or 45-degree bench). CRUCIAL given the L4/5 herniation + surgery: iron-proof the erectors. Controlled full ROM, spine neutral at the top (no snapping into hyperextension), never to failure, add load conservatively (hold a plate to the chest only once bodyweight sets are easy).',
  }),
];

export const EXERCISE_BY_ID: Map<string, Exercise> = new Map(EXERCISES.map((e) => [e.id, e]));
